import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Mail, Phone, MapPin, Clock, Send, CheckCircle,
  Calendar, ArrowLeft, ShoppingBag
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STORE_URL = window.location.hostname === 'store.oplogman.com' ? '/' : 'https://store.oplogman.com';

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";
export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { document.title = "Contact | Opulence Logistics Management"; }, []);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/leads/contact`, { ...form, source: "website_contact_form" });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-royal-blue-600 to-royal-blue-800 flex items-center justify-center shadow-lg shadow-royal-blue-600/30">
              <span className="text-white font-bold text-lg">O</span>
            </div>
            <div>
              <h1 className="font-cabinet text-xl font-bold tracking-tight text-slate-900">OpLogMan</h1>
              <p className="text-[10px] text-slate-400 font-manrope -mt-0.5">Opulence Logistics Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href={STORE_URL}>
              <Button variant="ghost" size="sm"><ShoppingBag size={16} className="mr-1" /> Store</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-royal-blue-900 to-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-cabinet text-4xl sm:text-5xl font-bold mb-3 tracking-tight">Get In Touch</h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Have a question or need our services? Reach out or book a consultation directly.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h3 className="font-cabinet text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            {submitted ? (
              <Card className="border-green-200 bg-green-50" data-testid="contact-success">
                <CardContent className="pt-8 pb-8 text-center">
                  <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
                  <h4 className="font-cabinet text-xl font-bold text-green-800 mb-2">Message Sent!</h4>
                  <p className="text-green-700">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <Button className="mt-6 bg-royal-blue-700 hover:bg-royal-blue-800" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" }); }}>
                    Send Another Message
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Smith" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input data-testid="contact-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@example.com" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="281-938-9601" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Your Company" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="What is this about?" />
                </div>
                <div className="space-y-2">
                  <Label>Message *</Label>
                  <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help..." />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-royal-blue-700 hover:bg-royal-blue-800 h-12" data-testid="contact-submit-btn">
                  {submitting ? "Sending..." : <><Send size={16} className="mr-2" /> Send Message</>}
                </Button>
              </form>
            )}

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              <Card>
                <CardContent className="pt-5 pb-4 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-royal-blue-100"><Mail size={18} className="text-royal-blue-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-sm font-medium">info@oplogman.com</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-green-100"><Phone size={18} className="text-green-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="text-sm font-medium">281-938-9601</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-amber-100"><Clock size={18} className="text-amber-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Business Hours</p>
                    <p className="text-sm font-medium">Mon - Fri, 9AM - 6PM EST</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-purple-100"><MapPin size={18} className="text-purple-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="text-sm font-medium">1250 W Sam Houston Pkwy S, Suite 302, Houston, TX 77042</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Booking Widget */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Calendar size={22} className="text-royal-blue-700" />
              <h3 className="font-cabinet text-2xl font-bold text-slate-900">Book a Consultation</h3>
            </div>
            <Card className="overflow-hidden border-royal-blue-200">
              <CardContent className="p-0">
                <iframe
                  src={BOOKING_URL}
                  title="Book a consultation with Opulence Logistics Management"
                  className="w-full border-0"
                  style={{ height: '620px', minHeight: '620px' }}
                  data-testid="booking-iframe"
                  allow="fullscreen"
                />
              </CardContent>
            </Card>
            <p className="text-xs text-slate-400 text-center mt-3">Powered by Microsoft 365 Bookings</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-royal-blue-500 to-royal-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">O</span>
              </div>
              <div>
                <span className="font-cabinet font-bold text-sm">OpLogMan</span>
                <span className="text-xs text-slate-500 ml-2">Opulence Logistics Management Corp.</span>
              </div>
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href={STORE_URL} className="hover:text-white transition-colors">Store</a>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy & Terms</Link>
            </div>
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
